import { Injectable } from "@angular/core";
import { IndividualConfig, ToastrService } from "ngx-toastr";
import { ErrorClass } from "@models/error";
import { Helper } from "@helpers/functions";
import { INFO_TOASTER_DURATION } from "@helpers/constants";
import { GoneError } from "bp-common-models-lib";
import { Router } from "@angular/router";
import { IframeV4EventsHandlers } from "@modules/iframeV4/services/iframeV4-eventHandler.service";
import { config } from "@config";

@Injectable({
	providedIn: "root"
})
export class InfoService {
	public constructor(
		private toastr: ToastrService,
		private readonly router: Router,
		private readonly IframeV4EventsHandlers: IframeV4EventsHandlers
	) {
		Helper.infoService = this;
		toastr.toastrConfig.preventDuplicates = true;
	}

	private readonly v4AppBaseUrl = config.V4AppBaseUrl;

	public setError(
		err: ErrorClass | string,
		options: Partial<IndividualConfig> & { ignore: string[] } = { ignore: [] },
		showOnlyWithoutMeta: boolean = false
	): void {
		const isRenderedInIframe = this.router.url.includes("iframe");
		if (err instanceof ErrorClass) {
			for (const e of err.responseErrors) {
				if ((showOnlyWithoutMeta && !e.meta && !this.ignoreCode(options, e.code)) || !showOnlyWithoutMeta) {
					if (isRenderedInIframe) {
						this.IframeV4EventsHandlers.postMessage(
							{ type: "ACTION_ERROR", notification: { state: "error", title: e.title } },
							this.v4AppBaseUrl
						);
					} else {
						this.toastr.error(e.title, err.name, options);
					}
				}
			}
		} else if (typeof err === "string") {
			if (isRenderedInIframe) {
				this.IframeV4EventsHandlers.postMessage(
					{ type: "ACTION_ERROR", notification: { state: "error", title: err } },
					this.v4AppBaseUrl
				);
			} else {
				this.toastr.error(err);
			}
		} else {
			console.error(err);
		}
	}

	public handleApiError(error: ErrorClass | Error): void {
		if (!(error instanceof ErrorClass)) {
			console.error(error);
			return;
		}

		if (!(error instanceof GoneError)) {
			this.setError(error);
			return;
		}

		for (const e of error.responseErrors) {
			this.setError(e.title);
		}
	}

	public setSuccess(info: string, sticky = false, duration: number = INFO_TOASTER_DURATION, title = ""): void {
		const isRenderedInIframe = this.router.url.includes("iframe");
		if (!sticky) {
			if (isRenderedInIframe) {
				this.IframeV4EventsHandlers.postMessage(
					{ type: "ACTION_SUCCESS", notification: { state: "success", title: info } },
					this.v4AppBaseUrl
				);
			} else {
				this.toastr.success(info, title, { timeOut: duration });
			}
		} else {
			if (isRenderedInIframe) {
				this.IframeV4EventsHandlers.postMessage(
					{ type: "ACTION_SUCCESS", notification: { state: "success", title: info } },
					this.v4AppBaseUrl
				);
			} else {
				this.toastr.success(info, title, {
					tapToDismiss: true,
					timeOut: duration
				});
			}
		}
	}

	public setInfo(info: string, title: string = "", duration: number = INFO_TOASTER_DURATION): void {
		const isRenderedInIframe = this.router.url.includes("iframe");
		if (isRenderedInIframe) {
			this.IframeV4EventsHandlers.postMessage(
				{ type: "ACTION_SUCCESS", notification: { state: "information", title: info } },
				this.v4AppBaseUrl
			);
		} else {
			this.toastr.info(info, title, { timeOut: duration });
		}
	}

	public setWarning(warning: string, sticky = false): void {
		const isRenderedInIframe = this.router.url.includes("iframe");
		if (!sticky) {
			if (isRenderedInIframe) {
				this.IframeV4EventsHandlers.postMessage(
					{ type: "ACTION_SUCCESS", notification: { state: "information", title: warning } },
					this.v4AppBaseUrl
				);
			} else {
				this.toastr.warning(warning);
			}
		} else {
			if (isRenderedInIframe) {
				this.IframeV4EventsHandlers.postMessage(
					{ type: "ACTION_SUCCESS", notification: { state: "information", title: warning } },
					this.v4AppBaseUrl
				);
			} else {
				this.toastr.warning(warning, "", { tapToDismiss: true });
			}
		}
	}

	public setSticky(message: string): void {
		this.toastr.error(message, "ERROR");
	}

	private ignoreCode(options: { ignore: string[] }, code: string): boolean {
		return options.ignore && options.ignore.indexOf(code) > -1;
	}

	public clearMessage(): void {
		this.toastr.clear();
	}
}
